import { CSSProperties, useEffect, useState } from 'react';
import {
  MultiControl,
  MultiSelectMenu,
  useMultiDropdown,
  ClearableFooter,
} from '@adsk/alloy-react-dropdown';
import styled, { CSSProp } from 'styled-components';
import noop from 'lodash/noop';
import Portal from '@adsk/alloy-react-portal';
import type { GenericRecord } from '../../types';
import { EMPTY_ARRAY, EMPTY_OBJECT } from '../../constants';

const MultiSelectDropdownWrapper = styled.div`
  width: 100%;
`;

export type MultiSelectDropdownProps<T extends GenericRecord> = {
  id?: string;
  options: Array<T>;
  selectedItems?: Array<T>;
  style?: CSSProperties;
  placeholder?: string;
  onChange: (selectedItems: Array<T>) => void;
  ['data-testid']?: string;
  disabled?: boolean;
  clearable?: boolean;
  className?: string;
  error?: boolean;
  isOpen?: boolean;
  onClose?: () => void;
};

export const MultiSelectDropdown = <T extends GenericRecord>({
  id,
  options,
  selectedItems = EMPTY_ARRAY,
  onChange,
  placeholder,
  style = {},
  'data-testid': dataTestId = 'MultiSelectDropdown',
  disabled,
  clearable,
  className,
  error,
  isOpen: isOpenProp = false,
  onClose = noop,
}: MultiSelectDropdownProps<T>) => {
  const [isMenuOpen, setIsMenuOpen] = useState(isOpenProp);
  useEffect(() => {
    setIsMenuOpen(isMenuOpen);
  }, [isMenuOpen]);

  const {
    isOpen,
    menuItems,
    getToggleButtonProps,
    getLabelProps,
    getMenuProps,
    getItemProps,
    getSelectedItemProps,
    addSelectedItem,
    removeSelectedItem,
    highlightedIndex,
    targetProps,
  } = useMultiDropdown({
    id,
    items: options,
    selectedItems,
    onSelectedItemsChange: (changes) => {
      onChange(changes.selectedItems || []);
    },
    isOpen: isMenuOpen,
    onIsOpenChange: ({ isOpen: _isOpen = false }) => {
      if (_isOpen === false) {
        onClose?.();
      }

      setIsMenuOpen(_isOpen);
    },
  });
  const showClearableFooter = clearable && selectedItems.length > 0;

  return (
    <MultiSelectDropdownWrapper css={style as CSSProp} className={className}>
      <MultiControl
        getSelectedItemProps={getSelectedItemProps}
        removeSelectedItem={removeSelectedItem}
        isOpen={isOpen}
        selectedItems={selectedItems}
        getToggleButtonProps={getToggleButtonProps}
        getLabelProps={getLabelProps}
        targetProps={targetProps}
        disabled={disabled}
        placeholder={placeholder}
        data-testid={dataTestId}
        error={error}
      />
      <Portal container={document.body}>
        <MultiSelectMenu
          isOpen={isOpen}
          menuItems={menuItems}
          targetProps={targetProps}
          highlightedIndex={highlightedIndex}
          selectedItems={selectedItems}
          getMenuProps={getMenuProps}
          getItemProps={getItemProps}
          addSelectedItem={addSelectedItem}
          removeSelectedItem={removeSelectedItem}
          renderFooter={() =>
            showClearableFooter ? (
              <ClearableFooter setSelectedItems={() => onChange([])} style={EMPTY_OBJECT} />
            ) : null
          }
        />
      </Portal>
    </MultiSelectDropdownWrapper>
  );
};
